import React, { useState } from 'react';
import {
  Button, Empty, Popconfirm, Space,
} from 'antd';
import Input from 'components/components/Input';
import DataColumns from 'components/components/DataColumns';
import DrawerFrame from 'components/components/Drawer/Frame';
import RequestItemFrame from 'components/RequestsOverview/components/RequestDetailsDrawer/components/OverviewTab/components/RequestItemCard/Frame';
import CardRow from 'components/RequestsOverview/components/RequestDetailsDrawer/components/OverviewTab/components/RequestItemCard/components/CardRow';
import { useChangePortalDraft, usePortalRequestItems, useSubmitPortalRequest } from 'packages/venture_builder_repository/portal/hooks';
import {
  PortalDraftChange, PortalRequest, PortalRequestItem, PortalScope,
} from 'packages/venture_builder_repository/portal/types';
import { officeRequestDetails } from 'packages/venture_builder_repository/portal/utils';
import showNotification from 'services/utils/showNotification';
import {
  LoadError, Loading, money, RequestTitle, startupLabel,
} from '../../shared';
import '../../VentureBuilderPortal.scss';

const popupContainer = (trigger: HTMLElement) => trigger.closest<HTMLElement>('.ant-drawer-content') || trigger;

const DraftQuantity = ({
  item, quantity, disabled, onChange, onSave,
}: {
  item: PortalRequestItem, quantity: number | null, disabled: boolean,
  onChange: (value: number | null) => void, onSave: (value: number) => void,
}) => {
  const valid = quantity !== null && Number.isSafeInteger(quantity) && quantity > 0;
  return (
    <Space direction="vertical">
      <Input layout="vertical">
        <Input.InputNumber
          aria-label={`Quantity for ${item.service?.name || `item #${item.id}`}`}
          value={quantity}
          min={1}
          precision={0}
          disabled={disabled}
          onChange={onChange}
        />
      </Input>
      {quantity !== item.quantity && (
        <>
          <Button disabled={disabled || !valid} onClick={() => { if (valid) onSave(quantity!); }}>Save quantity</Button>
          <Button disabled={disabled} onClick={() => onChange(item.quantity)}>Cancel quantity change</Button>
        </>
      )}
    </Space>
  );
};

const DraftRequestDrawer = ({ scope, request, onClose }: {
  scope: PortalScope, request: PortalRequest, onClose: () => void,
}) => {
  const items = usePortalRequestItems(scope, request.startup.id, request.id);
  const submit = useSubmitPortalRequest(scope);
  const change = useChangePortalDraft(scope);
  const [quantities, setQuantities] = useState<Record<number, number | null>>({});
  const hasEdits = items.data?.some((item) => (
    quantities[item.id] !== undefined && quantities[item.id] !== item.quantity
  ));
  const pending = submit.isPending || change.isPending;
  const busy = pending || items.isFetching;
  const edit = (action: PortalDraftChange['change']) => {
    if (!items.data || busy) return;
    submit.reset();
    change.mutate({
      startupId: request.startup.id, requestId: request.id, reviewedItems: items.data, change: action,
    }, {
      onSuccess: () => {
        if (action.kind === 'discard') onClose();
        else {
          setQuantities((previous) => {
            const next = { ...previous };
            delete next[action.itemId];
            return next;
          });
        }
      },
    });
  };
  return (
    <DrawerFrame
      className="drawer--blue"
      title={<RequestTitle items={items.data || []} status={request.status} />}
      open
      destroyOnClose
      onClose={() => { if (!pending) onClose(); }}
      closable={!pending}
      maskClosable={!pending}
      keyboard={!pending}
    >
      <div className="overview-tab-content vb-portal__request-details">
        <DataColumns columns={[
          { label: 'Startup', value: startupLabel(request.startup) },
          { label: 'Service provider', value: request.serviceProvider?.name || 'Not specified' },
        ]}
        />
        {items.isPending && <Loading />}
        {items.error && <LoadError error={items.error} retry={() => { items.refetch(); }} />}
        {!items.error && items.data && (
          <>
            {items.data.map((item) => (
              <RequestItemFrame key={item.id} title={item.service?.name || 'Service unavailable'}>
                {officeRequestDetails(item) && <CardRow label="Office" value={officeRequestDetails(item)!} />}
                {request.status === 1 ? (
                  <DraftQuantity
                    key={`${item.id}-${item.quantity}-${item.price}`}
                    item={item}
                    quantity={quantities[item.id] === undefined ? item.quantity : quantities[item.id]}
                    onChange={(value) => setQuantities((previous) => ({ ...previous, [item.id]: value }))}
                    disabled={busy}
                    onSave={(quantity) => edit({ kind: 'quantity', itemId: item.id, quantity })}
                  />
                ) : <CardRow label="Quantity" value={item.quantity} />}
                <CardRow label="Amount" value={money(item.price)} />
                {request.status === 1 && (
                  <Popconfirm
                    title="Remove this service from the draft?"
                    getPopupContainer={popupContainer}
                    disabled={busy}
                    onConfirm={() => edit({ kind: 'remove', itemId: item.id })}
                  >
                    <Button className="secondary-delete-button" danger disabled={busy}>Remove</Button>
                  </Popconfirm>
                )}
              </RequestItemFrame>
            ))}
            {!items.data.length && <Empty description="No services in this request" />}
            <div className="overview-tab__total">
              <span className="overview-tab__total-text">Total</span>
              <span className="overview-tab__total-amount">
                {money(items.data.reduce((total, item) => total + item.price, 0))}
              </span>
            </div>
            {hasEdits && <p>Save or cancel quantity changes before sending the request.</p>}
            {request.status === 1 && (
              <Space>
                <Button
                  className="primary-button"
                  loading={submit.isPending}
                  disabled={busy || hasEdits || !items.data.length}
                  onClick={() => {
                    change.reset();
                    submit.mutate({
                      startupId: request.startup.id, requestId: request.id, reviewedItems: items.data!,
                    }, {
                      onSuccess: () => { showNotification('Request sent for quotation'); onClose(); },
                    });
                  }}
                >
                  Send request
                </Button>
                <Popconfirm
                  title="Discard this draft and all its services?"
                  getPopupContainer={popupContainer}
                  disabled={busy}
                  onConfirm={() => edit({ kind: 'discard' })}
                >
                  <Button className="secondary-delete-button" danger disabled={busy} loading={change.isPending}>Discard draft</Button>
                </Popconfirm>
              </Space>
            )}
          </>
        )}
        {(submit.error || change.error) && (
          <div role="alert">
            {(submit.error || change.error)?.message}
          </div>
        )}
      </div>
    </DrawerFrame>
  );
};

export default DraftRequestDrawer;
